/* Native document scrolling. No wheel/touch interception or fixed positioning. */
{
  // Animation adds no hidden base styles: failed/disabled JavaScript stays visible.
  class DesktopProofReveal {
    constructor(host, selector) {
      this.host = host;
      this.targets = [...host.querySelectorAll(selector)];
      this.mobile = matchMedia('(max-width:749px)');
      this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
      this.animations = new Map();
      this.abort = new AbortController();
      this.configure = this.configure.bind(this);
      this.mobile.addEventListener('change', this.configure, { signal:this.abort.signal });
      this.reduced.addEventListener('change', this.configure, { signal:this.abort.signal });
      this.configure();
    }
    configure() {
      this.observer?.disconnect();
      this.animations.forEach(animation => animation.cancel());
      this.animations.clear();
      if (this.mobile.matches || this.reduced.matches || this.host.hasAttribute('data-editor')) return;
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          this.animations.get(entry.target)?.cancel();
          this.animations.delete(entry.target);
          if (entry.isIntersecting) {
            const animation = entry.target.animate([
              { opacity:0, transform:'translateY(16px)' },
              { opacity:1, transform:'translateY(0)' }
            ], { duration:600, easing:'cubic-bezier(0.22, 1, 0.36, 1)' });
            this.animations.set(entry.target, animation);
          }
        });
      }, { threshold:0, rootMargin:'0px 0px -24px 0px' });
      this.targets.forEach(target => this.observer.observe(target));
    }
    disconnect() {
      this.abort.abort();
      this.observer?.disconnect();
      this.animations.forEach(animation => animation.cancel());
      this.animations.clear();
    }
  }
  if (!customElements.get('fitfam-home-stats')) {
    customElements.define('fitfam-home-stats', class extends HTMLElement {
      connectedCallback() {
        this.panels = [...this.querySelectorAll('.ff-home-stats__panel')];
        this.dots = [...this.querySelectorAll('.ff-home-stats__progress span')];
        this.stage = this.querySelector('.ff-home-stats__stage');
        this.track = this.querySelector('.ff-home-stats__track');
        this.mobile = matchMedia('(max-width:749px)');
        this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
        this.abort = new AbortController();
        const options = { signal:this.abort.signal };
        this.configure = () => {
          if (!this.measureFrame) this.measureFrame = requestAnimationFrame(() => { this.measureFrame = 0; this.measure(); });
        };
        this.queue = () => {
          if (!this.frame && this.hasAttribute('data-story')) this.frame = requestAnimationFrame(() => { this.frame = 0; this.draw(); });
        };
        this.mobile.addEventListener('change', this.configure, options);
        this.reduced.addEventListener('change', this.configure, options);
        window.addEventListener('resize', this.configure, options);
        window.visualViewport?.addEventListener('resize', this.configure, options);
        window.addEventListener('pageshow', this.configure, options);
        this.observer = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            window.addEventListener('scroll', this.queue, { passive:true, signal:this.abort.signal });
            this.queue();
          } else window.removeEventListener('scroll', this.queue);
        }, { rootMargin:'100px' });
        this.observer.observe(this);
        this.resizeObserver = new ResizeObserver(() => { if (this.hasAttribute('data-story')) this.configure(); });
        this.resizeObserver.observe(this.stage);
        this.fade = new DesktopProofReveal(this, '.ff-home-stats__heading, .ff-home-stats__panel');
        this.configure();
        document.fonts?.ready.then(() => { if (this.isConnected) this.configure(); });
      }
      disableStory() {
        this.removeAttribute('data-story');
        this.style.removeProperty('--ff-stage-height');
        this.style.removeProperty('--ff-story-distance');
        this.panels.forEach(panel => { panel.style.removeProperty('--ff-stat-opacity'); panel.style.removeProperty('--ff-stat-y'); });
      }
      measure() {
        if (!this.mobile.matches || this.reduced.matches || this.hasAttribute('data-editor') || this.panels.length < 2) {
          this.disableStory();
          return;
        }
        this.setAttribute('data-story', '');
        const height = this.stage.getBoundingClientRect().height;
        const top = parseFloat(getComputedStyle(this.stage).top) || 0;
        const viewport = window.visualViewport?.height || window.innerHeight;
        // Use intrinsic content height, never a full viewport or a clipped stage.
        if (height + top + 8 > viewport) {
          this.disableStory();
          return;
        }
        this.step = Math.max(160, Math.min(360, Number(this.dataset.scrollStep) || 220));
        // Only a brief final reading hold; no extra full-stat spacer at the end.
        this.distance = this.step * (this.panels.length - 1) + this.step * .25;
        this.style.setProperty('--ff-stage-height', `${height}px`);
        this.style.setProperty('--ff-story-distance', `${this.distance}px`);
        this.queue();
      }
      draw() {
        const top = parseFloat(getComputedStyle(this.stage).top) || 0;
        const rect = this.track.getBoundingClientRect();
        const position = Math.max(0, Math.min(this.panels.length - 1, (top - rect.top) / this.step));
        const current = Math.floor(position);
        const blend = Math.max(0, (position - current - .38) / .62);
        const eased = blend * blend * (3 - 2 * blend);
        this.panels.forEach((panel, index) => {
          const opacity = index === current ? 1 - eased : index === current + 1 ? eased : 0;
          panel.style.setProperty('--ff-stat-opacity', opacity);
          panel.style.setProperty('--ff-stat-y', `${index === current ? -12 * eased : 12 * (1 - opacity)}px`);
        });
        this.dots.forEach((dot, index) => dot.classList.toggle('is-current', index === current));
      }
      disconnectedCallback() {
        this.abort?.abort();
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.fade?.disconnect();
        cancelAnimationFrame(this.frame);
        cancelAnimationFrame(this.measureFrame);
      }
    });
  }
  if (!customElements.get('fitfam-home-reviews')) {
    customElements.define('fitfam-home-reviews', class extends HTMLElement {
      connectedCallback() { this.fade = new DesktopProofReveal(this, '.multicolumn-card'); }
      disconnectedCallback() { this.fade?.disconnect(); }
    });
  }
}
